import { TranslateModule } from '@ngx-translate/core';
import { SlicePipe } from '@angular/common';
import { Subscription } from 'rxjs';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy,
  OnInit, Output, SimpleChanges, ViewEncapsulation
} from '@angular/core';

import {
  DeviceService, SdButtonComponent, SdIsCollapsibleDirective, SdListComponent, SdThemeComponent
} from '@seb/web-design-system/core';

@Component({
  selector: 'sd-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SdListComponent,
    SdButtonComponent,
    SlicePipe,
    TranslateModule,
    SdIsCollapsibleDirective
  ]
})
export class SdPaginationComponent extends SdThemeComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  linkUrl = '';

  @Input()
  context = 'Pagination';

  @Input()
  pages: number;

  @Input()
  activePage = 1;

  @Input()
  class: string;
  @HostBinding('class')
  get hostClasses(): string {
    return [this.class].filter(Boolean).join(' ');
  }

  @Output()
  clickedPage = new EventEmitter<number>();

  @Output()
  clickedPrevPage = new EventEmitter<void>();

  @Output()
  clickedNextPage = new EventEmitter<void>();

  pageNumber: { index: number }[];
  lastPage: number;
  firstPage: number;
  spliceStartValue: number;
  spliceEndValue: number;
  maxDisplayedPage = {
    desktop: 10,
    mobile: 5
  };
  _maxDisplayedPage: number;
  levels = [10, 100, 1000];
  arrayLevels = [];
  arrayLastLevels = [];
  lastLevel = 0;
  subscriptions = new Subscription();
  isMobile$: boolean;
  linkUrlNoParams: string;
  isOpen = false;
  showLevels = true;
  showLastLevel = true;
  nbPagesBeforeAfterCurrent;

  constructor(
    private cdRef: ChangeDetectorRef,
    private deviceService: DeviceService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.linkUrlNoParams = this.linkUrl.split('?')[0];
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((x) => {
        this.isMobile$ = x;
        this.updatePagination();
        this.cdRef.markForCheck();
      })
    );
  }

  clickGoToPage(event: Event, index: number): void {
    event.preventDefault();
    this.goToPage(index);
    this.clickedPage.emit(index);
  }

  goToPage(index: number): void {
    this.activePage = index;
    this.getPageToDisplay();
  }

  goToPreviousPage(event: Event): void {
    event.preventDefault();
    this.activePage = this.activePage - 1;
    this.clickedPrevPage.emit();
    this.clickedPage.emit(this.activePage);
    this.getPageToDisplay();
  }

  goToNextPage(event: Event): void {
    event.preventDefault();
    this.activePage = this.activePage + 1;
    this.clickedPage.emit(this.activePage);
    this.clickedNextPage.emit();
    this.getPageToDisplay();
  }

  private updatePagination(): void {
    this._maxDisplayedPage = this.isMobile$
      ? this.maxDisplayedPage.mobile
      : this.maxDisplayedPage.desktop;

    this.pageNumber = Array(this.pages).fill('').map((_, i) => ({ index: i + 1 }));

    this.lastPage = this.pageNumber?.slice(-1)[0].index;
    this.firstPage = this.pageNumber[0]?.index;

    if (this.activePage > this.lastPage) {
      this.activePage = this.lastPage;
    }

    this.goToPage(this.activePage);
  }

  private getPageToDisplay(): void {
    this.nbPagesBeforeAfterCurrent = this.isMobile$ ? 1 : 2;
    if (this.pageNumber.length <= this._maxDisplayedPage) {
      this.spliceStartValue = this.activePage <= 3 ? 0 : 1;
      this.spliceEndValue = undefined;
    } else {
      if (this.activePage - this.nbPagesBeforeAfterCurrent <= 0) {
        this.spliceStartValue = 0;
        this.spliceEndValue = this.activePage + 2;
      } else if (
        this.activePage + this.nbPagesBeforeAfterCurrent >=
        this.pages
      ) {
        this.spliceStartValue =
          this.activePage - (this.nbPagesBeforeAfterCurrent + 1);
        this.spliceEndValue = this.pages - 1;
      } else {
        this.spliceStartValue =
          this.activePage - (this.nbPagesBeforeAfterCurrent + 1);
        this.spliceEndValue = this.activePage + this.nbPagesBeforeAfterCurrent;
      }
    }
    if (!this.isMobile$) {
      if (this.pages > this.levels[0]) {
        this.arrayLevels = new Array(Math.floor(this.pages / 10));
        if (this.arrayLevels) {
          let numberOfLevelToDisplay;
          if (this.activePage > 3) {
            const strActivePage = this.activePage.toString();
            const lastDigit = strActivePage.charAt(strActivePage.length - 1);
            if (lastDigit === '8' || lastDigit === '9') {
              const increment =
                lastDigit === '8'
                  ? Math.floor((this.activePage + 2) / 10)
                  : Math.floor((this.activePage + 1) / 10);
              numberOfLevelToDisplay =
                this._maxDisplayedPage -
                (this.spliceEndValue - this.spliceStartValue - (increment - 2));
            } else {
              numberOfLevelToDisplay =
                this._maxDisplayedPage -
                (this.spliceEndValue - this.spliceStartValue + 2);
            }
          } else {
            numberOfLevelToDisplay =
              this._maxDisplayedPage -
              (this.spliceEndValue - this.spliceStartValue + 1);
          }
          const decrement = this.pages > 1000 ? 2 : this.pages > 100 ? 1 : 0;
          numberOfLevelToDisplay = numberOfLevelToDisplay - decrement;
          this.arrayLevels = this.arrayLevels.slice(0, numberOfLevelToDisplay);
          this.lastLevel =
            this.pages > 1000 ? 1000 : this.pages > 100 ? 100 : null;
          if (
            this.activePage + 2 === this.lastLevel ||
            this.activePage + 1 === this.lastLevel ||
            this.pages < 100
          ) {
            this.lastLevel = null;
          }
        }
      } else {
        this.lastLevel = null;
      }
    } else {
      if (this.activePage === 3) {
        this.spliceStartValue = 0;
      }
      this.arrayLevels = this.activePage < 3 && this.pages > 10 ? [10] : null;
    }
    this.showLevels =
      this.arrayLevels && this.arrayLevels?.length < this._maxDisplayedPage;

    this.arrayLastLevels = [];
    this.arrayLastLevels[0] = this.isMobile$
      ? 10
      : this.pages > 100
        ? 200
        : this.arrayLevels?.length * 10 + 10;

    for (let i = 0; this.arrayLastLevels[i] < this.pages; i++) {
      const increment = this.arrayLastLevels[i] + 10 <= 100 ? 10 : 100;
      this.arrayLastLevels.push(this.arrayLastLevels[i] + increment);
    }

    this.arrayLastLevels = this.arrayLastLevels.filter((value) => {
      return (
        value < this.pages &&
        this.activePage + this.nbPagesBeforeAfterCurrent < value
      );
    });
    if (this.isMobile$ && this.activePage < 3)
      this.arrayLastLevels = this.arrayLastLevels.filter(
        (value) => value !== 10,
      );
    if (this.arrayLastLevels?.length <= 0) this.isOpen = false;

    this.showLastLevel =
      this.lastLevel &&
      this.activePage < this.lastLevel &&
      this.pageNumber[this.spliceEndValue - 1].index !== this.lastLevel &&
      this.arrayLevels?.length * 10 !== this.lastLevel;
  }
}
